"use client"
import type { UseDisclosure } from "@/lib/hooks/useDisclosure"
import { cn } from "@/lib/tailwind"
import * as ModalPrimitive from "@radix-ui/react-dialog"
import * as React from "react"
import { CloseButton } from "./CloseButton"

const ModalRoot = ModalPrimitive.Root

const ModalTrigger = ModalPrimitive.Trigger

function ModalPortal({ children, isCentered, ...props }: ModalPrimitive.DialogPortalProps & { isCentered?: boolean }) {
  return (
    <ModalPrimitive.Portal {...props}>
      <div className={cn("fixed inset-0 z-50 flex flex-col items-center justify-start pt-4", isCentered && "justify-center")}>
        {children}
      </div>
    </ModalPrimitive.Portal>
  )
}
ModalPortal.displayName = ModalPrimitive.Portal.displayName

const ModalOverlay = React.forwardRef<
  React.ElementRef<typeof ModalPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof ModalPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <ModalPrimitive.Overlay
    className={cn(
      "data-[state=open]:fade-in data-[state=closed]:fade-out fixed inset-0 z-50 bg-black/50 transition-all duration-100 data-[state=closed]:animate-out",
      className,
    )}
    {...props}
    ref={ref}
  />
))
ModalOverlay.displayName = ModalPrimitive.Overlay.displayName

const ModalContent = React.forwardRef<
  React.ElementRef<typeof ModalPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof ModalPrimitive.Content> & { isCentered?: boolean }
>(({ className, children, isCentered, ...props }, ref) => (
  <ModalPortal isCentered={isCentered}>
    <ModalOverlay />
    <ModalPrimitive.Content
      ref={ref}
      className={cn(
        "data-[state=open]:fade-in-90 data-[state=open]:slide-in-from-bottom-10 sm:zoom-in-90 data-[state=open]:sm:slide-in-from-bottom-0 scrollbar-hide fixed z-50 grid max-h-[95vh] w-full animate-in gap-4 overflow-x-hidden overflow-y-scroll rounded-md bg-white p-4 px-6 dark:bg-gray-800",
        className,
      )}
      onInteractOutside={(e) => {
        // hack to prevent closing when interacting with jodit editor
        const isJodit = e.composedPath().some((el: EventTarget) => {
          if ("classList" in el) return Array.from((el as Element).classList).includes("jodit")
          return false
        })
        const isMultiSelect = e.composedPath().some((el: EventTarget) => {
          console.log(el)

          if ("classList" in el) return Array.from((el as Element).classList).includes("react-multi-select-portal")
          return false
        })
        if (isJodit || isMultiSelect) {
          e.preventDefault()
        }
      }}
      {...props}
    >
      {children}
      <ModalPrimitive.Close
        className="absolute top-4 right-4 rounded opacity-70 transition-opacity hover:opacity-100 focus:outline-none disabled:pointer-events-none"
        asChild
      >
        <CloseButton />
      </ModalPrimitive.Close>
    </ModalPrimitive.Content>
  </ModalPortal>
))
ModalContent.displayName = ModalPrimitive.Content.displayName

function ModalHeader({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("flex flex-col text-center sm:text-left", className)} {...props} />
}

function ModalFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)} {...props} />
}

const ModalTitle = React.forwardRef<
  React.ElementRef<typeof ModalPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof ModalPrimitive.Title>
>(({ className, ...props }, ref) => (
  <ModalPrimitive.Title
    ref={ref}
    className={cn("font-semibold text-gray-900 text-lg", "dark:text-gray-50", className)}
    {...props}
  />
))

const ModalDescription = React.forwardRef<
  React.ElementRef<typeof ModalPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof ModalPrimitive.Description>
>(({ className, ...props }, ref) => (
  <ModalPrimitive.Description ref={ref} className={cn("text-gray-500 text-sm", "dark:text-gray-400", className)} {...props} />
))

export { ModalRoot, ModalTrigger, ModalContent, ModalHeader, ModalFooter, ModalTitle, ModalDescription }

export interface ModalProps extends Partial<UseDisclosure> {
  title?: React.ReactNode
  description?: React.ReactNode
  children?: React.ReactNode
  trigger?: React.ReactNode
  size?: "sm" | "md" | "lg" | "xl" | "2xl" | "3xl" | "4xl" | "5xl" | "6xl" | "full"
  isCentered?: boolean
}

export function Modal({ size = "md", trigger, description, children, title, isCentered, ...disclosureProps }: ModalProps) {
  return (
    <ModalRoot modal open={disclosureProps.isOpen} onOpenChange={disclosureProps.onClose}>
      {trigger && <ModalTrigger asChild>{trigger}</ModalTrigger>}
      <ModalContent
        title=""
        translate="no"
        isCentered={isCentered}
        className={cn(
          "notranslate",
          size === "sm" && "max-w-sm",
          size === "md" && "max-w-md",
          size === "lg" && "max-w-lg",
          size === "xl" && "max-w-xl",
          size === "2xl" && "max-w-2xl",
          size === "3xl" && "max-w-3xl",
          size === "4xl" && "max-w-4xl",
          size === "5xl" && "max-w-5xl",
          size === "6xl" && "max-w-6xl",
          size === "full" && "max-w-full",
        )}
      >
        {title ? (
          <ModalHeader>
            <ModalHeader>{title && <ModalTitle asChild={typeof title !== "string"}>{title}</ModalTitle>}</ModalHeader>
            <ModalDescription className={cn(!description && "hidden")}>{description}</ModalDescription>
          </ModalHeader>
        ) : (
          <ModalTitle className="invisible hidden" />
        )}
        <div className="h-full">{children}</div>
      </ModalContent>
    </ModalRoot>
  )
}
